import { createMarkup } from '@packages/domain/utils/createMarkup/createMarkup';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import { LoadingDots } from 'components/ui/LoadingDots/LoadingDots';
import { useLRSContext } from 'contexts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Back } from '../../components/Back/Back';
import { AdditionalInfo } from './AdditionalInfo/AdditionalInfo';
import { Step } from './components';
import { payloadAdapter } from './payloadAdapter';
import './Summary.scss';

export const Summary = () => {
  const {
    completed,
    i18n,
    idVerificationNeeded,
    isStudentPaying,
    navigation,
    onCancel,
    onSubmit,
    steps,
    values,
    previousTransactions,
  } = useLRSContext();
  const isMountedRef = useRef(true);
  const isCompleted = useCallback(
    step => completed.includes(step.id),
    [completed],
  );
  const isNotSummary = (step: Record<string, unknown>) => step.id !== 'summary';
  const restSteps = steps.filter(isNotSummary);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEachStepCompleted, _setIsEachStepCompleted] = useState(
    restSteps.every(isCompleted),
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  async function handleClick() {
    if (!isMountedRef.current) {
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = await payloadAdapter(
        { ...values, idVerificationNeeded, isStudentPaying },
        previousTransactions || [],
      );
      await onSubmit(payload);
    } catch (_e) {
      // do nothing
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }

  return (
    <div className="Summary flex flex-col min-h-full">
      <div className="flex-1">
        <Back onClick={() => onCancel()} className="marginBottom-md" />
        <Heading as="h2" size="large" className="fontWeight-semiBold">
          {i18n.t('checkout.LRS.summary.title')}
        </Heading>
        <p
          dangerouslySetInnerHTML={createMarkup(
            i18n.t('checkout.LRS.summary.text'),
          )}
        />
        <ol className="Summary-steps">
          {restSteps.map(step => (
            <Step
              key={step.id}
              onClick={() => navigation.go(step.id)}
              isCompleted={completed.includes(step.id)}
              i18n={i18n}
              {...step}
            />
          ))}
        </ol>
      </div>
      <div className="mt-auto pt-6 pb-4">
        <Button
          onClick={handleClick}
          tone="primary"
          size="full"
          disabled={!isEachStepCompleted || isSubmitting}
          data-testid="submit"
        >
          {isSubmitting ? (
            <LoadingDots />
          ) : (
            i18n.t('checkout.LRS.summary.button')
          )}
        </Button>
      </div>
      <AdditionalInfo />
    </div>
  );
};
